/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { LogProvider } from './Interfaces';
import { Logger } from './Logger';

/**
 * Util that encapsulates lifecycle of active trouter URL and manages waiter for the URL via promise
 */
export class TrouterUrlPromise {
    private url?: string;
    private pendingPromise?: Promise<string>;
    // eslint-disable-next-line @typescript-eslint/ban-types
    private pendingPromiseResolveRef?: Function;
    // eslint-disable-next-line @typescript-eslint/ban-types
    private pendingPromiseRejectRef?: Function;
    private logger: Logger;

    public constructor(logProvider: LogProvider) {
        this.logger = new Logger('UrlPromise', logProvider);
    }

    public getPromise(): Promise<string> {
        if (this.url !== undefined) {
            this.logger.debug(`returning previously resolved url: ${this.url}`);

            return Promise.resolve(this.url);
        }

        if (this.pendingPromise === undefined) {
            this.logger.debug('creating and returning promise');
            this.pendingPromise = new Promise<string>((resolve, reject) => {
                this.pendingPromiseResolveRef = resolve;
                this.pendingPromiseRejectRef = reject;
            });
        } else {
            this.logger.debug('returning existing promise');
        }

        return this.pendingPromise;
    }

    public resolveUrl(url: string) {
        this.url = url;
        this.logger.debug(`got url: ${this.url}`);

        const resolveCallback = this.pendingPromiseResolveRef;
        this.pendingPromise = undefined;
        this.pendingPromiseResolveRef = undefined;
        this.pendingPromiseRejectRef = undefined;

        if (resolveCallback !== undefined) {
            this.logger.debug('resolving promise');
            resolveCallback(url);
        }
    }

    public rejectUrl(error: Error) {
        this.logger.debug('aborting');
        const rejectCallback = this.pendingPromiseRejectRef;
        this.url = undefined;
        this.pendingPromise = undefined;
        this.pendingPromiseResolveRef = undefined;
        this.pendingPromiseRejectRef = undefined;

        if (rejectCallback !== undefined) {
            this.logger.debug('rejecting promise');
            rejectCallback(error);
        }
    }

    public resetUrl() {
        this.logger.debug('resetting url');
        this.url = undefined;
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterUrlPromise.ts